// ✅ Zero dependências extras — editor rico nativo (contentEditable + execCommand)
// Compatível com React 19, sem findDOMNode, sem wrappers problemáticos.
import React, { useCallback, useEffect, useRef, useState } from "react"
import { useOutletContext } from "react-router-dom"
import axios from "axios"
import {
  FaBold, FaItalic, FaUnderline, FaStrikethrough,
  FaListUl, FaListOl, FaQuoteLeft,
  FaAlignLeft, FaAlignCenter, FaAlignRight,
  FaLink, FaImage, FaEraser,
  FaPlus, FaSave, FaTimes, FaEdit, FaTrash,
} from "react-icons/fa"

const API = "https://api.ticketdovaqueiro.com"

const CATEGORIAS = [
  { value: "geral",      label: "Geral"       },
  { value: "destaque",   label: "Destaque"    },
  { value: "vaquejadas", label: "Vaquejadas"  },
  { value: "campeonato", label: "Campeonatos" },
  { value: "ranking",    label: "Ranking"     },
  { value: "avisos",     label: "Avisos"      },
]

/* ════════════════════════════════════════════════════════════
   EDITOR RICO — contentEditable nativo, sem dependências
   ════════════════════════════════════════════════════════════ */
function EditorRico({ value, onChange }) {
  const editorRef  = useRef(null)
  const fileRef    = useRef(null)
  const isFirstMount = useRef(true)

  /* Inicializa o HTML apenas no mount */
  useEffect(() => {
    if (editorRef.current) {
      editorRef.current.innerHTML = value || ""
    }
  }, []) // eslint-disable-line

  /* Sincroniza quando value muda externamente (ao clicar Editar notícia) */
  const prevValue = useRef(value)
  useEffect(() => {
    if (!isFirstMount.current && value !== prevValue.current) {
      prevValue.current = value
      if (editorRef.current && document.activeElement !== editorRef.current) {
        editorRef.current.innerHTML = value || ""
      }
    }
    isFirstMount.current = false
  }, [value])

  const emit = useCallback(() => {
    const html = editorRef.current?.innerHTML || ""
    onChange(html === "<br>" ? "" : html)
  }, [onChange])

  /* Executa um comando de formatação */
  const exec = useCallback((cmd, arg = null) => {
    editorRef.current?.focus()
    document.execCommand(cmd, false, arg)
    emit()
  }, [emit])

  /* Estado ativo dos botões */
  const [active, setActive] = useState({})
  const refreshActive = () => {
    try {
      setActive({
        bold:          document.queryCommandState("bold"),
        italic:        document.queryCommandState("italic"),
        underline:     document.queryCommandState("underline"),
        strike:        document.queryCommandState("strikeThrough"),
        ul:            document.queryCommandState("insertUnorderedList"),
        ol:            document.queryCommandState("insertOrderedList"),
        justifyLeft:   document.queryCommandState("justifyLeft"),
        justifyCenter: document.queryCommandState("justifyCenter"),
        justifyRight:  document.queryCommandState("justifyRight"),
      })
    } catch (_) {}
  }

  /* Inserir link */
  const insertLink = () => {
    const url = window.prompt("URL do link:", "https://")
    if (url) exec("createLink", url)
  }

  /* Inserir imagem por upload */
  const handleFileUpload = async (e) => {
    const file = e.target.files[0]
    if (!file) { e.target.value = ""; return }
    try {
      const fd = new FormData()
      fd.append("file", file)
      const token = localStorage.getItem("token")
      const { data } = await axios.post(`${API}/api/upload`, fd, {
        headers: { Authorization: `Bearer ${token}` },
      })
      const url = data.url || `${API}/uploads/${data.filename}`
      editorRef.current?.focus()
      document.execCommand("insertHTML", false,
        `<img src="${url}" style="max-width:100%;border-radius:10px;margin:12px 0;display:block;" alt=""/>`)
      emit()
    } catch {
      const url = window.prompt("Não foi possível fazer upload. Cole a URL da imagem:")
      if (url) exec("insertHTML", `<img src="${url}" style="max-width:100%;border-radius:10px;margin:12px 0;display:block;" alt=""/>`)
    } finally {
      e.target.value = ""
    }
  }

  /* Helpers de UI */
  const Btn = ({ icon, cmd, arg, title, activeKey }) => (
    <button
      type="button" title={title}
      onMouseDown={e => { e.preventDefault(); cmd ? exec(cmd, arg) : arg?.() }}
      style={{
        display: "inline-flex", alignItems: "center", justifyContent: "center",
        width: 30, height: 30, borderRadius: 6, border: "none",
        background: active[activeKey] ? "rgba(255,204,0,0.18)" : "transparent",
        color:      active[activeKey] ? "#ffcc00" : "rgba(255,255,255,0.65)",
        cursor: "pointer", fontSize: 12, flexShrink: 0,
        transition: "background 0.15s, color 0.15s",
      }}
    >{icon}</button>
  )

  const Sep = () => (
    <div style={{ width: 1, height: 20, background: "rgba(255,255,255,0.1)", margin: "0 3px", flexShrink: 0 }} />
  )

  const selectStyle = {
    background: "#2a2a2a", border: "1px solid rgba(255,255,255,0.12)",
    borderRadius: 6, color: "rgba(255,255,255,0.75)",
    padding: "0 6px", height: 28, fontSize: 12,
    cursor: "pointer", outline: "none", maxWidth: 110,
  }

  return (
    <div style={{ border: "1px solid rgba(255,255,255,0.12)", borderRadius: 12, overflow: "hidden" }}>

      {/* ── Toolbar ── */}
      <div style={{
        display: "flex", flexWrap: "wrap", alignItems: "center", gap: 2,
        padding: "8px 10px",
        background: "#1e1e1e",
        borderBottom: "1px solid rgba(255,255,255,0.09)",
      }}>

        {/* Estilo do bloco */}
        <select
          style={selectStyle}
          defaultValue=""
          onMouseDown={e => e.preventDefault()}
          onChange={e => {
            exec("formatBlock", e.target.value)
            e.target.value = ""
          }}
        >
          <option value="" disabled>Estilo</option>
          <option value="p">Parágrafo</option>
          <option value="h1">Título 1</option>
          <option value="h2">Título 2</option>
          <option value="h3">Título 3</option>
          <option value="blockquote">Citação</option>
          <option value="pre">Código</option>
        </select>

        <Sep />
        <Btn icon={<FaBold />}          cmd="bold"              title="Negrito (Ctrl+B)"    activeKey="bold"    />
        <Btn icon={<FaItalic />}         cmd="italic"            title="Itálico (Ctrl+I)"    activeKey="italic"  />
        <Btn icon={<FaUnderline />}      cmd="underline"         title="Sublinhado (Ctrl+U)" activeKey="underline"/>
        <Btn icon={<FaStrikethrough />}  cmd="strikeThrough"     title="Tachado"             activeKey="strike"  />

        <Sep />
        <Btn icon={<FaListUl />}  cmd="insertUnorderedList" title="Lista com marcadores" activeKey="ul" />
        <Btn icon={<FaListOl />}  cmd="insertOrderedList"   title="Lista numerada"       activeKey="ol" />
        <Btn icon={<FaQuoteLeft />} cmd="formatBlock" arg="blockquote" title="Citação" />

        <Sep />
        <Btn icon={<FaAlignLeft />}   cmd="justifyLeft"   title="Alinhar à esquerda" activeKey="justifyLeft"   />
        <Btn icon={<FaAlignCenter />} cmd="justifyCenter" title="Centralizar"         activeKey="justifyCenter" />
        <Btn icon={<FaAlignRight />}  cmd="justifyRight"  title="Alinhar à direita"  activeKey="justifyRight"  />

        <Sep />
        <Btn icon={<FaLink />}   title="Inserir link"   arg={insertLink} />
        <Btn
          icon={<FaImage />} title="Inserir imagem (upload)"
          arg={() => fileRef.current?.click()}
        />
        <input
          ref={fileRef} type="file" accept="image/*"
          onChange={handleFileUpload}
          style={{ display: "none" }}
        />

        <Sep />
        <Btn icon={<FaEraser />} cmd="removeFormat" title="Limpar formatação" />
      </div>

      {/* ── Área de texto ── */}
      <div
        ref={editorRef}
        contentEditable
        suppressContentEditableWarning
        data-placeholder="Escreva o conteúdo da notícia aqui..."
        onInput={emit}
        onBlur={emit}
        onKeyUp={refreshActive}
        onMouseUp={refreshActive}
        style={{
          minHeight: 320, padding: "20px 24px",
          background: "#141414",
          outline: "none",
          color: "rgba(255,255,255,0.9)",
          fontSize: 15, lineHeight: 1.85,
          fontFamily: "Inter, -apple-system, sans-serif",
          caretColor: "#ffcc00",
        }}
      />

      {/* Estilos internos do editor */}
      <style>{`
        [data-placeholder]:empty::before {
          content: attr(data-placeholder);
          color: rgba(255,255,255,0.18);
          pointer-events: none;
        }
        [contenteditable] h1 { font-size: 1.9em; font-weight: 900; color: #fff; margin: 1em 0 0.4em; }
        [contenteditable] h2 { font-size: 1.5em; font-weight: 800; color: #fff; margin: 0.9em 0 0.4em; }
        [contenteditable] h3 { font-size: 1.2em; font-weight: 700; color: rgba(255,255,255,0.92); margin: 0.8em 0 0.35em; }
        [contenteditable] blockquote {
          border-left: 4px solid #ffcc00; margin: 1em 0; padding: 10px 18px;
          background: rgba(255,204,0,0.05); border-radius: 0 8px 8px 0;
          color: rgba(255,255,255,0.72); font-style: italic;
        }
        [contenteditable] pre {
          background: rgba(255,255,255,0.06); border-radius: 8px;
          padding: 12px 18px; font-family: monospace; font-size: 14px;
          white-space: pre-wrap;
        }
        [contenteditable] a { color: #ffcc00; }
        [contenteditable] ul, [contenteditable] ol { padding-left: 26px; margin: 0.6em 0; }
        [contenteditable] li { margin-bottom: 5px; }
        [contenteditable] img { max-width: 100%; border-radius: 10px; margin: 10px 0; }
        [contenteditable] p { margin: 0 0 0.9em; }
      `}</style>
    </div>
  )
}

/* ════════════════════════════════════════════════════════════
   PAINEL DE NOTÍCIAS
   ════════════════════════════════════════════════════════════ */
export default function CircuitoNoticiasPainel() {
  const { circuito }    = useOutletContext()
  const circuitoId      = circuito?._id
  const fileRef         = useRef(null)

  const [noticias,     setNoticias]     = useState([])
  const [loading,      setLoading]      = useState(false)
  const [titulo,       setTitulo]       = useState("")
  const [subtitulo,    setSubtitulo]    = useState("")
  const [categoria,    setCategoria]    = useState("geral")
  const [conteudoHtml, setConteudoHtml] = useState("")
  const [imagem,       setImagem]       = useState(null)
  const [previewImg,   setPreviewImg]   = useState(null)
  const [editandoId,   setEditandoId]   = useState(null)

  const fetchNoticias = async () => {
    try {
      const res = await axios.get(`${API}/api/circuitos/${circuitoId}/noticias`)
      setNoticias(res.data || [])
    } catch (err) { console.error(err) }
  }

  useEffect(() => { if (circuitoId) fetchNoticias() }, [circuitoId])

  const resetForm = () => {
    setTitulo(""); setSubtitulo(""); setCategoria("geral")
    setConteudoHtml(""); setImagem(null); setPreviewImg(null); setEditandoId(null)
  }

  const handleImagemCapa = (e) => {
    const file = e.target.files[0]
    if (!file) return
    setImagem(file)
    setPreviewImg(URL.createObjectURL(file))
  }

  const criarOuEditar = async () => {
    if (!titulo.trim()) return alert("Informe o título da notícia")
    setLoading(true)
    try {
      const fd = new FormData()
      fd.append("titulo",       titulo)
      fd.append("subtitulo",    subtitulo)
      fd.append("categoria",    categoria)
      fd.append("conteudoHtml", conteudoHtml)
      if (imagem) fd.append("imagem", imagem)

      if (editandoId) {
        await axios.put(`${API}/api/circuitos/${circuitoId}/noticias/${editandoId}`, fd)
        alert("Notícia atualizada!")
      } else {
        await axios.post(`${API}/api/circuitos/${circuitoId}/noticias`, fd)
        alert("Notícia publicada!")
      }
      resetForm(); fetchNoticias()
    } catch (err) {
      alert(err?.response?.data?.erro || "Erro ao salvar notícia")
    } finally {
      setLoading(false)
    }
  }

  const editarNoticia = async (noticiaId) => {
    try {
      const res = await axios.get(`${API}/api/circuitos/${circuitoId}/noticias/${noticiaId}`)
      const n = res.data
      setEditandoId(n._id); setTitulo(n.titulo || "")
      setSubtitulo(n.subtitulo || ""); setCategoria(n.categoria || "geral")
      setConteudoHtml(n.conteudoHtml || "")
      setImagem(null)
      setPreviewImg(n.imagem ? `${API}/uploads/${n.imagem}` : null)
      window.scrollTo({ top: 0, behavior: "smooth" })
    } catch { alert("Erro ao carregar notícia para edição") }
  }

  const deletarNoticia = async (id) => {
    if (!window.confirm("Excluir essa notícia?")) return
    try {
      await axios.delete(`${API}/api/circuitos/${circuitoId}/noticias/${id}`)
      fetchNoticias()
    } catch { alert("Erro ao deletar notícia") }
  }

  return (
    <div>
      <div className="pc-page-header">
        <h1 className="pc-page-header__title">Notícias</h1>
        <p className="pc-page-header__subtitle">Gerencie as notícias do circuito {circuito?.nome}</p>
      </div>

      {/* ── Formulário ── */}
      <div className="pc-card">
        <div className="pc-card__header">
          <h3 className="pc-card__title">
            {editandoId
              ? <><FaEdit style={{ marginRight: 10 }} />Editar Notícia</>
              : <><FaPlus style={{ marginRight: 10 }} />Criar Notícia</>
            }
          </h3>
        </div>

        <div className="pc-form-row">
          <div className="pc-form-group">
            <label className="pc-label">Título *</label>
            <input type="text" className="pc-input" value={titulo}
              onChange={e => setTitulo(e.target.value)} placeholder="Título da notícia" />
          </div>
          <div className="pc-form-group">
            <label className="pc-label">Subtítulo</label>
            <input type="text" className="pc-input" value={subtitulo}
              onChange={e => setSubtitulo(e.target.value)} placeholder="Subtítulo ou resumo" />
          </div>
        </div>

        <div className="pc-form-group" style={{ maxWidth: 260 }}>
          <label className="pc-label">Categoria</label>
          <select className="pc-select" value={categoria} onChange={e => setCategoria(e.target.value)}>
            {CATEGORIAS.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
          </select>
        </div>

        <div className="pc-form-group">
          <label className="pc-label">Imagem de capa</label>
          <div style={{ display: "flex", gap: 14, alignItems: "flex-start", flexWrap: "wrap" }}>
            <button type="button" onClick={() => fileRef.current?.click()}
              className="pc-btn pc-btn--secondary pc-btn--sm">
              <FaImage style={{ marginRight: 6 }} />
              {previewImg ? "Trocar imagem" : "Selecionar imagem"}
            </button>
            <input ref={fileRef} type="file" accept="image/*"
              onChange={handleImagemCapa} style={{ display: "none" }} />
            {previewImg && (
              <div style={{ position: "relative" }}>
                <img src={previewImg} alt="preview"
                  style={{ height: 80, borderRadius: 10, objectFit: "cover",
                    border: "1px solid rgba(255,255,255,0.1)" }} />
                <button onClick={() => { setImagem(null); setPreviewImg(null) }}
                  style={{
                    position: "absolute", top: -8, right: -8, width: 22, height: 22,
                    borderRadius: "50%", background: "rgba(239,68,68,0.9)", border: "none",
                    color: "#fff", cursor: "pointer", fontSize: 11,
                    display: "flex", alignItems: "center", justifyContent: "center",
                  }}>✕</button>
              </div>
            )}
          </div>
        </div>

        <div className="pc-form-group">
          <label className="pc-label">
            Conteúdo da notícia
            <span style={{ fontWeight: 400, color: "rgba(255,255,255,0.3)", marginLeft: 8, fontSize: "0.8em" }}>
              negrito · títulos · listas · citação · imagens · links
            </span>
          </label>
          <EditorRico value={conteudoHtml} onChange={setConteudoHtml} />
        </div>

        <div className="pc-btn-group" style={{ marginTop: 16 }}>
          <button onClick={criarOuEditar} disabled={loading} className="pc-btn pc-btn--primary">
            <FaSave />
            {loading ? "Salvando..." : editandoId ? "Salvar Alterações" : "Publicar Notícia"}
          </button>
          {editandoId && (
            <button onClick={resetForm} className="pc-btn pc-btn--secondary">
              <FaTimes /> Cancelar
            </button>
          )}
        </div>
      </div>

      {/* ── Lista ── */}
      <div className="pc-card">
        <div className="pc-card__header">
          <h3 className="pc-card__title">Notícias Cadastradas</h3>
        </div>
        {noticias.length === 0 ? (
          <div className="pc-empty">Nenhuma notícia cadastrada ainda.</div>
        ) : (
          <div className="pc-list">
            {noticias.map(n => (
              <div key={n._id} className="pc-list-item">
                {n.imagem && (
                  <img src={`${API}/uploads/${n.imagem}`} alt=""
                    style={{ width: 72, height: 54, objectFit: "cover",
                      borderRadius: 8, flexShrink: 0 }} />
                )}
                <div className="pc-list-item__content" style={{ flex: 1 }}>
                  <h4 className="pc-list-item__title">{n.titulo}</h4>
                  <p className="pc-list-item__subtitle">{n.subtitulo || "Sem subtítulo"}</p>
                  <div className="pc-list-item__meta">
                    Categoria: <strong>{n.categoria || "geral"}</strong>
                  </div>
                </div>
                <div className="pc-list-item__actions">
                  <button onClick={() => editarNoticia(n._id)} className="pc-btn pc-btn--info pc-btn--sm">
                    <FaEdit /> Editar
                  </button>
                  <button onClick={() => deletarNoticia(n._id)} className="pc-btn pc-btn--danger pc-btn--sm">
                    <FaTrash /> Excluir
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}